import React, { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Send, Loader2, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AiraTestPage = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(true);
  const [history, setHistory] = useState([]);

  const testPrompts = [
    { label: 'Flight Search', prompt: 'Book a flight from Delhi to Goa on 25 December' },
    { label: 'Hotel Search', prompt: 'Find hotels in Mumbai for 2 adults from 15 September to 17 September' },
    { label: 'Web Search', prompt: 'Search Shah Rukh Khan' },
    { label: 'Greeting', prompt: 'Hello AIRA, how are you?' },
    { label: 'Finance', prompt: 'How much did I spend this month?' },
    { label: 'Task', prompt: 'Remind me to call mom tomorrow' },
  ];

  const getAuthToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'test@aira.com', password: 'Test123!' }),
      });
      const data = await response.json();
      return data.access_token;
    } catch (err) {
      console.error('Auth error:', err);
      return null;
    }
  }, []);

  const sendMessage = useCallback(async (inputMessage) => {
    const msgToSend = inputMessage || message;
    if (!msgToSend.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Failed to authenticate');
      }

      const startTime = performance.now();
      const response = await fetch(`${API_URL}/api/aira/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: msgToSend }),
      });

      const data = await response.json();
      const endTime = performance.now();

      const resultWithMeta = {
        ...data,
        _meta: {
          responseTime: Math.round(endTime - startTime),
          timestamp: new Date().toISOString(),
          inputMessage: msgToSend,
        },
      };

      setResult(resultWithMeta);
      setHistory((prev) => [resultWithMeta, ...prev].slice(0, 10));
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [message, getAuthToken]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const getStatusBadge = (success) => {
    if (success === true) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3" /> Success
        </span>
      );
    } else if (success === false) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3" /> Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <AlertCircle className="w-3 h-3" /> Unknown
      </span>
    );
  };

  const getCapabilityStatus = (result) => {
    if (!result) return null;
    
    const intent = result.intent || 'unknown';
    const action = result.action_taken;
    const hasData = result.data && Object.keys(result.data).length > 0;
    
    // Determine if real data was returned
    let dataStatus = 'NO_DATA';
    if (hasData) {
      if (result.data.flights?.length > 0 || result.data.hotels?.length > 0) {
        dataStatus = 'REAL_API_DATA';
      } else if (result.data.sources?.length > 0) {
        dataStatus = 'SEARCH_RESULTS';
      } else {
        dataStatus = 'STRUCTURED_DATA';
      }
    } else if (result.response && result.response.length > 50) {
      dataStatus = 'AI_RESPONSE';
    }
    
    return { intent, action, dataStatus };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" data-testid="aira-test-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="aira-test-title">
            AIRA Echo Testing Console
          </h1>
          <p className="text-gray-600 mt-1">
            Internal testing environment for AIRA Echo capabilities
          </p>
        </div>

        {/* Quick Test Prompts */}
        <Card data-testid="quick-prompts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">Quick Test Prompts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {testPrompts.map((item, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMessage(item.prompt);
                    sendMessage(item.prompt);
                  }}
                  disabled={loading}
                  data-testid={`quick-prompt-${idx}`}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Input Form */}
        <Card data-testid="input-card">
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message to AIRA..."
                disabled={loading}
                className="flex-1"
                data-testid="message-input"
              />
              <Button type="submit" disabled={loading || !message.trim()} data-testid="send-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50" data-testid="error-card">
            <CardContent className="pt-4">
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Result Display */}
        {result && (
          <Card data-testid="result-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-700">Response</CardTitle>
                {getStatusBadge(result.success)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Response */}
              <div className="bg-gray-50 rounded-lg p-4" data-testid="response-content">
                <p className="text-gray-800 whitespace-pre-wrap">{result.response}</p>
              </div>

              {/* Debug Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDebug(!showDebug)}
                className="w-full justify-center text-gray-500"
                data-testid="debug-toggle"
              >
                {showDebug ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                {showDebug ? 'Hide' : 'Show'} Debug Information
              </Button>

              {/* Debug Information */}
              {showDebug && (
                <div className="space-y-4 border-t pt-4" data-testid="debug-section">
                  {/* Status Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600 font-medium">Intent</p>
                      <p className="text-sm font-semibold text-blue-900" data-testid="debug-intent">
                        {result.intent || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <p className="text-xs text-purple-600 font-medium">Action</p>
                      <p className="text-sm font-semibold text-purple-900" data-testid="debug-action">
                        {result.action_taken || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-green-600 font-medium">Response Time</p>
                      <p className="text-sm font-semibold text-green-900" data-testid="debug-response-time">
                        {result._meta?.responseTime}ms
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3">
                      <p className="text-xs text-orange-600 font-medium">Data Status</p>
                      <p className="text-sm font-semibold text-orange-900" data-testid="debug-data-status">
                        {getCapabilityStatus(result)?.dataStatus || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Entities */}
                  {result.entities && Object.keys(result.entities).length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 font-medium mb-2">Extracted Entities</p>
                      <pre className="text-xs text-gray-800 overflow-x-auto" data-testid="debug-entities">
                        {JSON.stringify(result.entities, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Data */}
                  {result.data && Object.keys(result.data).length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 font-medium mb-2">Response Data</p>
                      <pre className="text-xs text-gray-800 overflow-x-auto max-h-60" data-testid="debug-data">
                        {JSON.stringify(result.data, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Session Info */}
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                    <div>
                      <span className="font-medium">Session ID:</span> {result.session_id?.slice(0, 8)}...
                    </div>
                    <div>
                      <span className="font-medium">Conversation ID:</span> {result.conversation_id?.slice(0, 8)}...
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Test History */}
        {history.length > 1 && (
          <Card data-testid="history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Recent Tests ({history.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.slice(1, 6).map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                    data-testid={`history-item-${idx}`}
                  >
                    <span className="text-gray-600 truncate max-w-[200px]">
                      {item._meta?.inputMessage}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{item.intent}</span>
                      {getStatusBadge(item.success)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Integration Status Note */}
        <div className="text-center text-xs text-gray-500 p-4" data-testid="status-note">
          <p>
            <strong>Note:</strong> Travel (Flights & Hotels) uses <strong>REAL TripJack API</strong>.
            Finance and Productivity features currently provide <strong>AI-assisted guidance</strong> (no real data integration yet).
          </p>
        </div>
      </div>
    </div>
  );
};

export default AiraTestPage;
